import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { SAMPLE } from "./sampleData";
import { apiGet } from "./api";

const STORE_KEY = "mwy_construction_hse_live_blank_v1";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HSEContext = createContext(null);

async function loadFromBackend() {
  try {
    const res = await axios.get(`${API}/state`, { timeout: 10000 });
    return res.data?.data || null;
  } catch (e) {
    return null;
  }
}

async function saveToBackend(data) {
  try {
    await axios.put(`${API}/state`, { data }, { timeout: 10000 });
    return true;
  } catch (e) {
    return false;
  }
}

async function resetBackend() {
  try { await axios.delete(`${API}/state`, { timeout: 10000 }); } catch {}
}

export function HSEProvider({ children }) {
  // Bootstrap synchronously from localStorage cache for instant paint
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.schemaVersion === SAMPLE.schemaVersion) return parsed;
      }
    } catch {}
    return SAMPLE;
  });

  const [filters, setFilters] = useState({
    month: "All",
    year: String(new Date().getFullYear()),
    site: "All Sites",
    department: "All Departments",
    region: "All Regions",
    checklistType: "All Checklist Types",
    equipmentCategory: "All Equipment Categories",
    status: "All Statuses",
    riskLevel: "All Risk Levels",
    search: "",
  });
  const [lastRefreshed, setLastRefreshed] = useState(new Date().toISOString());
  const [syncStatus, setSyncStatus] = useState("loading"); // loading | synced | offline | saving
  const saveTimer = useRef(null);
  const isInitialLoad = useRef(true);

  // Load from backend on mount (overrides local cache if backend has newer data)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const remote = await loadFromBackend();
      if (cancelled) return;
      if (remote && remote.schemaVersion === SAMPLE.schemaVersion) {
        setData(remote);
        setSyncStatus("synced");
      } else if (remote && remote.schemaVersion !== SAMPLE.schemaVersion) {
        // backend has old schema â€” push current local state up
        setSyncStatus("synced");
      } else {
        setSyncStatus("synced");
      }
      isInitialLoad.current = false;
    })();
    return () => { cancelled = true; };
  }, []);

  // Persist locally on every change (instant)
  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(data)); } catch {}
  }, [data]);

  // Debounced save to backend so data survives across devices/refreshes
  useEffect(() => {
    if (isInitialLoad.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSyncStatus("saving");
    saveTimer.current = setTimeout(async () => {
      const ok = await saveToBackend(data);
      setSyncStatus(ok ? "synced" : "offline");
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [data]);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const resetSample = async () => {
    await resetBackend();
    localStorage.removeItem(STORE_KEY);
    setData(SAMPLE);
    setLastRefreshed(new Date().toISOString());
  };

  const refresh = async () => {
    setSyncStatus("loading");
    const remote = await loadFromBackend();
    if (remote && remote.schemaVersion === SAMPLE.schemaVersion) {
      setData(remote);
    }
    setLastRefreshed(new Date().toISOString());
    setSyncStatus("synced");
  };

  const siteNames = useMemo(() => {
    const arr = data.settings?.sites || [];
    if (arr.length && typeof arr[0] === "object") return arr.map((s) => s.number || s.name);
    return arr;
  }, [data.settings?.sites]);

  const activeSites = useMemo(() => {
    const arr = data.settings?.sites || [];
    if (arr.length && typeof arr[0] === "object") return arr.filter((s) => s.status === "Active").map((s) => s.number);
    return arr;
  }, [data.settings?.sites]);

  const value = useMemo(() => ({
    data, setData, update, filters, setFilters,
    lastRefreshed, refresh, resetSample,
    siteNames, activeSites, syncStatus,
  }), [data, filters, lastRefreshed, siteNames, activeSites, syncStatus]);

  return <HSEContext.Provider value={value}>{children}</HSEContext.Provider>;
}

export function useHSE() {
  const ctx = useContext(HSEContext);
  if (!ctx) throw new Error("useHSE must be used inside HSEProvider");
  return ctx;
}

export function applyFilter(rows, filters, dateField = "date") {
  const siteRegionByName = {};
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : SAMPLE;
    (parsed.settings?.sites || SAMPLE.settings.sites || []).forEach((site) => {
      if (typeof site === "object") siteRegionByName[site.number || site.name] = site.region;
    });
  } catch {
    (SAMPLE.settings?.sites || []).forEach((site) => {
      siteRegionByName[site.number || site.name] = site.region;
    });
  }

  return rows.filter((r) => {
    if (filters.site !== "All Sites" && r.site && r.site !== filters.site) return false;
    if (filters.department !== "All Departments" && r.department && r.department !== filters.department) return false;
    if (filters.region !== "All Regions" && r.site && siteRegionByName[r.site] && siteRegionByName[r.site] !== filters.region) return false;
    if (filters.checklistType && filters.checklistType !== "All Checklist Types" && r.type && r.type !== filters.checklistType) return false;
    if (filters.equipmentCategory && filters.equipmentCategory !== "All Equipment Categories" && r.category && r.category !== filters.equipmentCategory && r.equipmentCategory !== filters.equipmentCategory) return false;
    if (filters.status && filters.status !== "All Statuses" && r.status && r.status !== filters.status) return false;
    if (filters.riskLevel && filters.riskLevel !== "All Risk Levels" && (r.risk || r.riskLevel || r.priority) && ![r.risk, r.riskLevel, r.priority].includes(filters.riskLevel)) return false;
    if (filters.month !== "All" && r[dateField]) {
      const m = new Date(r[dateField]).getMonth();
      if (m !== Number(filters.month)) return false;
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const hit = Object.values(r).some((v) => String(v).toLowerCase().includes(q));
      if (!hit) return false;
    }
    return true;
  });
}


